import { Currency } from 'api/account';
import { HttpClient } from 'api/http-client';
import { ProjectFinancialsContractType } from 'api/project';
import { LinkedResource } from 'api/tasks';
import { ObjectType } from 'components/organisms/module-settings/integrations/integrations-list/salesforce/resource-mappings/constants';
import { IntegrationField } from '../types';
import {
  ChildResourcesResponse,
  ContactFieldSyncDetailConfig,
  CreateAutomationPayload,
  GetMappableSalesforceFieldsParams,
  MappableSalesforceFieldsResponse,
  SalesforceActions,
  SalesforceActionsV2,
  SalesforceAutomation,
  SalesforceConfig,
  SalesforceFieldMapping,
  SalesforceIntegrationProvider,
  SalesforceObject,
  SalesforceResourceMapping,
  SalesforceSettingsMeta,
  SaveContactFieldSyncPayload,
  SaveContactFieldSyncResponse,
  SfInstallations,
  UpdateAutomationPayload,
} from './types';

const base = '/integrations/salesforce';

interface GetAuthDetailsPayload {
  params?: Record<any, any>;
}

class SalesforceHTTPClient extends HttpClient {
  private get v2BaseURL() {
    return this.BASEURL.replace(/\/v1$/, '/v2');
  }

  public getAuthDetails = ({
    params,
  }: GetAuthDetailsPayload): Promise<SalesforceIntegrationProvider> =>
    this.get<SalesforceIntegrationProvider>(
      '/integration-providers/salesforce/meta',
      {
        params,
      },
    );

  public disconnectIntegration = (integrationId: number): Promise<void> =>
    this.delete<void>(`${base}/${integrationId}`);

  public getIntegrationConfig = (): Promise<SalesforceConfig[]> =>
    this.get<SalesforceConfig[]>(base);

  public getIntegrationConfigById = (
    integrationId: number,
  ): Promise<SalesforceConfig> =>
    this.get<SalesforceConfig>(`${base}/${integrationId}`);

  public getTriggerObjects = (
    integrationId: number,
  ): Promise<SalesforceObject[]> =>
    this.get<SalesforceObject[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects`,
    );

  public getAutomationTriggerFields = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/trigger-condition-fields`,
    );

  public getAutomationMatchFields = (
    integrationId: number,
    salesforceObject: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/matching-condition-fields`,
    );

  public getSalesforceActionFields = (
    integrationId: number,
    salesforceObject: string,
    actionType: SalesforceActions,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/action-objects/${actionType}/action-input-fields`,
    );

  public getSalesforceActionFieldsV2 = (
    integrationId: number,
    salesforceObject: string,
    actionType: SalesforceActions | SalesforceActionsV2,
    triggerObjectParentPlaceholder?: string,
  ): Promise<IntegrationField[]> => {
    return this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/action-objects/${actionType}/action-input-fields`,
      {
        baseURL: this.v2BaseURL,
        params: triggerObjectParentPlaceholder
          ? { triggerObjectParentPlaceholder }
          : undefined,
      },
    );
  };

  public getChildResources = (
    integrationId: number,
    triggerObject: string,
    triggerObjectParentPlaceholder?: string,
  ): Promise<ChildResourcesResponse> => {
    return this.get<ChildResourcesResponse>(
      `${base}/${integrationId}/automations-builder/trigger-objects/${triggerObject}/related-resources`,
      {
        baseURL: this.v2BaseURL,
        params: triggerObjectParentPlaceholder
          ? { triggerObjectParentPlaceholder }
          : undefined,
      },
    );
  };

  public getContactMandatoryFields = (
    integrationId: number,
    contactObject: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/automations-builder/validation/contact-object/${contactObject}/mandatory-fields`,
      {
        baseURL: this.v2BaseURL,
      },
    );

  public getAutomations = (): Promise<SalesforceAutomation[]> =>
    this.get('/automations');

  public getAutomationsV2 = (): Promise<SalesforceAutomation[]> => {
    return this.get<(SalesforceAutomation & { id?: number })[]>(
      '/automations',
      { baseURL: this.v2BaseURL },
    ).then((automations) =>
      automations.map((automation) => ({
        ...automation,
        automationId: automation.automationId ?? automation.id,
      })),
    );
  };

  /**
   * @deprecated
   */
  public getAutomationById = (
    automationId: number,
  ): Promise<SalesforceAutomation> => this.get(`/automations/${automationId}`);

  /**
   * @deprecated Use createAutomationV2 when SalesforceV2 feature is enabled.
   */
  public createAutomation = (
    payload: CreateAutomationPayload,
  ): Promise<SalesforceAutomation> => this.post('/automations', payload);

  public createAutomationV2 = (
    payload: CreateAutomationPayload,
  ): Promise<SalesforceAutomation> =>
    this.post('/automations', payload, { baseURL: this.v2BaseURL });

  /**
   * @deprecated Use updateAutomationV2 when SalesforceV2 feature is enabled.
   */
  public updateAutomation = (
    payload: UpdateAutomationPayload,
  ): Promise<SalesforceAutomation> =>
    this.put(`/automations/${payload.automationId}`, payload);

  public updateAutomationV2 = (
    payload: UpdateAutomationPayload,
  ): Promise<SalesforceAutomation> =>
    this.put(`/automations/${payload.automationId}`, payload, {
      baseURL: this.v2BaseURL,
    });

  /**
   * @deprecated
   */
  public deleteAutomation = (automaionId: number): Promise<void> =>
    this.delete(`/automations/${automaionId}`);

  public getTemplatePlaceholders = (
    integrationId: string,
    templateId: any,
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> =>
    this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?templateId=${templateId}`,
    );

  public getFinancialsFields = (
    integrationId: string,
    billingMethod: ProjectFinancialsContractType,
    currencyCode: Currency['currencyCode'],
    salesforceObject: string | number | undefined,
  ): Promise<IntegrationField[]> =>
    this.get(
      `${base}/${integrationId}/automations-builder/trigger-objects/${salesforceObject}/dynamic-action-input-fields?financialContractType=${billingMethod}&currencyCode=${currencyCode}`,
    );

  /**
   * @deprecated
   */
  public reorderAutomation = (
    automationId: number,
    automationNewOrder: number,
  ): Promise<SalesforceAutomation[]> =>
    this.put(`/automations/${automationId}/reorder`, {
      order: automationNewOrder,
    });

  public getResourceMappings = (
    integrationId: number,
    selectedObject: ObjectType,
  ): Promise<SalesforceResourceMapping[]> =>
    this.get<SalesforceResourceMapping[]>(
      `${base}/${integrationId}/resource-mapping-config?
nativeResourceType=${selectedObject.toString()}`,
    );

  public getExternalResourceTypes = (
    integrationId: number,
  ): Promise<SalesforceObject[]> =>
    this.get<SalesforceObject[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types`,
    );

  public getAllExternalResourceTypes = (
    integrationId: number,
  ): Promise<Record<string, SalesforceObject[]>> => {
    return this.get<Record<string, SalesforceObject[]>>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types`,
      {
        baseURL: this.v2BaseURL,
      },
    );
  };

  public getExternalResourceFields = (
    integrationId: number,
    externalResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types/${externalResourceType}/fields`,
    );

  public getNativeResourceTypes = (
    integrationId: number,
  ): Promise<SalesforceResourceMapping[]> =>
    this.get<SalesforceResourceMapping[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types`,
    );

  public getNativeResourceFields = (
    integrationId: number,
    nativeResourceType: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${nativeResourceType}/fields`,
    );

  public getMappableNativeResourceFields = (
    integrationId: number,
    selectedObject: ObjectType,
    externalResourceType: string,
    externalResourceFieldId: string,
  ): Promise<IntegrationField[]> =>
    this.get<IntegrationField[]>(
      `${base}/${integrationId}/field-mapping-config-builder/native-resource-types/${selectedObject.toString()}/mappable-fields?externalResourceType=${externalResourceType}&externalFieldId=${externalResourceFieldId}`,
    );

  public createFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    payload: SalesforceFieldMapping,
  ): Promise<SalesforceFieldMapping> =>
    this.post(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config`,
      payload,
    );

  public deleteFieldMapping = (
    integrationId: number,
    resourceId: number,
    fieldMappingId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}/field-mapping-config/${fieldMappingId}`,
    );

  public updateFieldMapping = (
    integrationId: number,
    resourceObjectId: number,
    fieldMappingId: number,
    payload: SalesforceFieldMapping,
  ): Promise<SalesforceFieldMapping> =>
    this.put(
      `${base}/${integrationId}/resource-mapping-config/${resourceObjectId}/field-mapping-config/${fieldMappingId}`,
      payload,
    );

  public createResourceObject = (
    integrationId: number,
    payload: Pick<
      SalesforceResourceMapping,
      | 'externalIntegrationType'
      | 'externalResourceType'
      | 'nativeResourceType'
      | 'syncType'
      | 'enabled'
    >,
  ): Promise<SalesforceResourceMapping> =>
    this.post(`${base}/${integrationId}/resource-mapping-config/`, payload);

  public deleteResourceObject = (
    integrationId: number,
    resourceId: number,
  ): Promise<void> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping-config/${resourceId}`,
    );

  public linkSalesforceObject = (
    integrationId,
    payload,
  ): Promise<LinkedResource> =>
    this.post(`${base}/${integrationId}/api/sobject/resource-mapping`, payload);

  public linkSalesforceObjectV2 = (
    integrationId: number,
    selectedObject: ObjectType,
    payload: {
      nativeResourceId: number;
      sObjectUrl: string;
    },
  ): Promise<LinkedResource> => {
    return this.post(
      `${base}/${integrationId}/api/${selectedObject.toString()}/sobject/resource-mapping`,
      payload,
      {
        baseURL: this.v2BaseURL,
      },
    );
  };

  public getSalesforceObjectWithProject = (
    integrationId,
    externalResourceId,
    externalResourceType,
  ): Promise<any> =>
    this.get(
      `${base}/${integrationId}/api/sobject/${externalResourceType}/${externalResourceId}`,
    );

  public getBulkSalesforceObjects = (
    integrationId: number,
    sObjectType: string,
    sObjectIds: string[],
  ): Promise<Record<string, SalesforceObject>> =>
    this.get<Record<string, SalesforceObject>>(
      `${base}/${integrationId}/api/bulk/sobject/${sObjectType}`,
      {
        baseURL: this.v2BaseURL,
        params: { sObjectIds: sObjectIds.join(',') },
      },
    );

  public unLinkSalesforceObjectWithProject = (
    integrationId,
    resourceMappingId,
  ): Promise<SalesforceObject> =>
    this.delete(
      `${base}/${integrationId}/resource-mapping/${resourceMappingId}`,
    );

  public getSalesForceInstallations(
    integrationId: number,
  ): Promise<SfInstallations[]> {
    return this.get(`${base}/${integrationId}/app-installations`);
  }

  public updateSalesforceIntegrationSettings = (
    integrationId: number,
    payload: SalesforceSettingsMeta,
  ): Promise<SalesforceSettingsMeta> =>
    this.put(`${base}/${integrationId}/settings`, payload);

  public getContactFieldSyncDetail = (
    integrationId: number,
    contactObject: string,
  ): Promise<ContactFieldSyncDetailConfig> => {
    return this.get<ContactFieldSyncDetailConfig>(
      `${base}/${integrationId}/resource-mapping-config/contact/${contactObject}/mandatory-fields-config`,
    );
  };

  public getMappableSalesforceFields = ({
    integrationId,
    externalResourceType,
    nativeResourceType,
    nativeFieldName,
  }: GetMappableSalesforceFieldsParams): Promise<MappableSalesforceFieldsResponse> => {
    return this.get<MappableSalesforceFieldsResponse>(
      `${base}/${integrationId}/field-mapping-config-builder/external-resource-types/${externalResourceType}/mappable-fields`,
      {
        baseURL: this.v2BaseURL,
        params: {
          nativeResourceType,
          nativeFieldName,
        },
      },
    );
  };

  public saveContactFieldSync = (
    integrationId: number,
    payload: SaveContactFieldSyncPayload,
  ): Promise<SaveContactFieldSyncResponse> => {
    return this.post(
      `${base}/${integrationId}/resource-mapping-config/contact/mandatory-fields-config`,
      payload,
    );
  };
}

export const SalesforceAPI = new SalesforceHTTPClient();
